import { useState, useEffect } from "react";
import { invoke, SubscriptionResult } from "../lib/bridge";
import { useChat } from "../hooks/useChat";
import { useVisibility } from "../hooks/useVisibility";
import { DecryptedAvatar } from "./DecryptedAvatar";
import { ChatBox } from "./ChatBox";

interface MemberInfo {
  id: number;
  displayName: string;
  role: string;
  online: boolean;
  lastActiveAt: string | null;
  isSelf: boolean;
  avatarUrl: string | null;
}

export function lastOnlineLabel(iso: string | null): string {
  if (!iso) return "Offline";
  const mins = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  if (mins < 1) return "Last online just now";
  if (mins < 60) return `Last online ${mins} ${mins === 1 ? "minute" : "minutes"} ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `Last online ${hours} ${hours === 1 ? "hour" : "hours"} ago`;
  const days = Math.floor(hours / 24);
  return `Last online ${days} ${days === 1 ? "day" : "days"} ago`;
}

interface FamilyTabProps {
  memberName: string;
  memberId: number;
  authToken: string | null;
  apiUrl: string;
  onOpenMember: (name: string, avatarUrl: string | null, online: boolean, lastActiveAt: string | null) => void;
  onInvite: () => void;
  onJoinFamily: () => void;
  onHelp: () => void;
  onAccount: () => void;
}

export function FamilyTab({ memberName, memberId, authToken, apiUrl, onOpenMember, onInvite, onJoinFamily, onHelp, onAccount }: FamilyTabProps) {
  const [members, setMembers] = useState<MemberInfo[]>([
    { id: 0, displayName: memberName, role: "keeper", online: true, lastActiveAt: null, isSelf: true, avatarUrl: null },
  ]);
  const [memberLimit, setMemberLimit] = useState<number | null>(null);
  const [billingEnabled, setBillingEnabled] = useState(false);

  const visible = useVisibility();
  const { messages, sendMessage } = useChat({
    apiUrl, authToken, channel: "family", memberId, active: visible,
  });

  useEffect(() => {
    invoke<MemberInfo[]>("list_members")
      .then(setMembers)
      .catch((e) => console.error("Failed to list members:", e));

    const interval = setInterval(() => {
      invoke<MemberInfo[]>("list_members")
        .then(setMembers)
        .catch(() => {});
    }, 30000);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    invoke<SubscriptionResult>("get_subscription")
      .then((s) => {
        setBillingEnabled(s.enabled);
        setMemberLimit(s.memberLimit);
      })
      .catch(() => {});
  }, []);

  const memberAvatars = new Map(members.map((m) => [m.id, m.avatarUrl]));
  const atCap = billingEnabled && memberLimit != null && members.length >= memberLimit;

  return (
    <div className="container family-screen">
      <div className="header-row">
        <h1 className="header-title">Family</h1>
        <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
          {billingEnabled && <button className="btn-secondary btn-small" onClick={onAccount}>Account</button>}
          <button className="btn-secondary btn-small" onClick={onHelp}>Help</button>
          <button className="btn-secondary btn-small" onClick={onJoinFamily}>Join</button>
          <button
            className="btn-add"
            onClick={atCap ? onAccount : onInvite}
            aria-label={atCap ? "Upgrade to add members" : "Invite member"}
            title={atCap ? "You've reached your plan's member limit — upgrade to add more" : undefined}
          >
            +
          </button>
        </div>
      </div>

      <div className="family-columns">
        <ul className="member-list family-members-col">
          {members.map((member) => (
            <li key={member.id} className="member-item" onClick={() => onOpenMember(member.displayName, member.avatarUrl, member.online, member.lastActiveAt)}>
              <div className="avatar-badge-wrap">
                <DecryptedAvatar token={member.avatarUrl} name={member.displayName} size="small" />
                <span className={`online-dot-badge ${member.online ? "online" : "offline"}`} />
              </div>
              <div className="member-info">
                <span className="member-name">
                  {member.displayName}
                  {member.isSelf && <span className="member-you-hint">This is you</span>}
                </span>
                <span className="last-active">
                  {member.online ? "Now" : lastOnlineLabel(member.lastActiveAt)}
                </span>
              </div>
              <span className="member-chevron">&rsaquo;</span>
            </li>
          ))}
        </ul>

        <div className="family-chat-col">
          <ChatBox
            messages={messages}
            memberAvatars={memberAvatars}
            onSend={sendMessage}
          />
        </div>
      </div>
    </div>
  );
}
